import React from 'react';
import clsx from 'clsx';
import { EllipsisVertical, GripVertical } from 'lucide-react';
import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@dashboard/components/ui/accordion';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import usePage from '@dashboard/hooks/usePage';
import { useFeeEdit } from './FeeEditContext';
import { feeId, getFeeFormValues, getFeeSummary } from './feeFormValues';
import FeeEdit from './FeeEdit';
import useFeeHelpers from './useFeeHelpers';

export default function FeeItem({ fee, dragHandleProps }) {
  const { page } = usePage();
  const { isAddingFee } = useFeeEdit();

  const id = feeId(fee);
  const { onDuplicateFee, onDeleteFee } = useFeeHelpers(id);
  const summary = getFeeSummary(getFeeFormValues(fee, page), page);

  return (
    <AccordionItem value={id} className="border-b-0">
      <div className="sticky top-0 bg-white dark:bg-background z-10 flex flex-row items-center gap-2 px-1 rounded-t">
        <div
          {...dragHandleProps}
          className={clsx(
            buttonVariants({ variant: 'ghost', size: 'icon' }),
            'cursor-grab active:cursor-grabbing',
          )}
        >
          <GripVertical className="size-4" />
        </div>

        <div className="flex-1 min-w-0">
          <AccordionTrigger
            className={clsx('py-3 items-center min-w-0 hover:no-underline', {
              'cursor-not-allowed': isAddingFee,
            })}
            disabled={fee.isNew}
          >
            {fee.isNew ? (
              <Skeleton aria-hidden="true" className="h-4 w-28" />
            ) : (
              <span className="font-medium truncate shrink-0 max-w-[60%]">{fee.name}</span>
            )}

            {fee.isNew && <Skeleton aria-hidden="true" className="h-3.5 w-16 ml-auto" />}
            {!fee.isNew && (
              <div className="flex flex-col items-end ml-auto min-w-0 text-right">
                {summary.map(line => (
                  <span
                    key={line}
                    className={clsx(
                      'text-gray-500 whitespace-nowrap truncate max-w-full',
                      summary.length > 1 ? 'text-xs' : 'text-sm',
                    )}
                  >
                    {line}
                  </span>
                ))}
              </div>
            )}
          </AccordionTrigger>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={isAddingFee}>
            <ShadcnButton
              variant="ghost"
              size="icon"
              aria-label="More options"
              disabled={isAddingFee}
            >
              <EllipsisVertical className="size-4" />
            </ShadcnButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onDuplicateFee}>Duplicate</DropdownMenuItem>
            <DropdownMenuItem onSelect={onDeleteFee} variant="destructive">
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AccordionContent className="pb-4 pt-0 relative">
        <FeeEdit fee={fee} />
      </AccordionContent>
    </AccordionItem>
  );
}
